/**
 * In-browser exporter — produces the downloadable zip from a DesignSystem.
 *
 * Pipeline:
 *   1. Take the built dist artifacts (JS bundle + the three CSS source files).
 *   2. Run each through substitute() with the user's prefix.
 *   3. Generate {prefix}-brand.css from the user's knob values.
 *   4. Generate app/{prefix}.js + app/{prefix}-overrides.css from the contract.
 *   5. Assemble the zip with the chosen flavor (modular | minified).
 *   6. Include AGENTS.md, skills/, README.md, design-system.json.
 *   7. Trigger browser download.
 *
 * Zip layout — three ownership lines, one rule each:
 *
 *   vendor/                    READ-ONLY. Byte-identical for a given version +
 *     {prefix}-ui.js           flavor + icon set, regardless of knobs. Upgrade =
 *     {prefix}-ui.d.ts         replace this folder.
 *     css/{prefix}-*.css
 *     docs/manifest.json, {prefix}-*.md
 *   {prefix}-brand.css         GENERATED from design-system.json. Don't hand-edit;
 *   {prefix}-design-system.json   change it in the customizer and re-export.
 *   assets/, favicon*          brand uploads (referenced by brand.css)
 *   app/                       YOURS. Shipped once, never overwritten by an
 *     {prefix}.js              upgrade. Every hook, commented out — the
 *     {prefix}-overrides.css   initializer. Diff a fresh export's app/ against
 *                              yours to see what's new.
 *   README.md, AGENTS.md, skills/
 */
import JSZip from 'jszip';
import { substitute, substitutePath } from '../../scripts/substitute-prefix.mjs';
import { type DesignSystem, tokensToCss } from './state.js';
import { generateAppJs, generateOverridesCss, type ManifestComponent } from './generate-app.js';

import readmeTemplate from './templates/README.md?raw';
import skillTemplate from './templates/skills/ry-ui-builder/SKILL.md?raw';

// Bundle the built dist artifacts directly into the customizer at build time.
// `?raw` bypasses Vite's transform pipeline so we get the actual file contents
// as plain strings — no module wrapping, no source-map injection, no HMR shim.
//
// Four JS variants exist: one per icon set × (mangled | readable). The export
// picks by `ds.tokens.icons.set` so the package ships exactly one set, and by
// flavor — `modular` ships the readable build (`class RyElement`, real field
// names: vendor source you can open and follow), `minified` the mangled one.
import rawJsFomantic from '../../dist/ry-ui.js?raw';
import rawJsLucide from '../../dist/ry-ui-lucide.js?raw';
import rawJsFomanticReadable from '../../dist/ry-ui.readable.js?raw';
import rawJsLucideReadable from '../../dist/ry-ui-lucide.readable.js?raw';
import rawCssTokens from '../../dist/css/ry-tokens.css?raw';
import rawCssStructure from '../../dist/css/ry-structure.css?raw';
import rawCssTheme from '../../dist/css/ry-theme.css?raw';
import rawCssBundle from '../../dist/css/ry-ui.css?raw';
import rawDts from '../../dist/ry-ui.d.ts?raw';

// Compiled component contracts — derived from each ry-*.ts source by
// scripts/docs/compile-docs.mjs. Auto-generated; never edited.
// AGENTS.md is the LLM-targeted catalog; per-component .md files are
// the Fomantic-style reference. Both ship inside every export zip so
// consumers (and their agents) have the contracts that match the code.
import rawAgents from '../../dist/docs/AGENTS.md?raw';
import rawManifest from '../../dist/docs/manifest.json?raw';
const componentDocs = import.meta.glob('../../dist/docs/ry-*.md', {
  eager: true, query: '?raw', import: 'default',
}) as Record<string, string>;

export type ExportFlavor = 'modular' | 'minified';

interface BuildAssets {
  js: string;
  cssTokens: string;
  cssStructure: string;
  cssTheme: string;
  cssBundle: string;
  dts: string;
}

function loadAssets(iconSet: 'fomantic' | 'lucide', flavor: ExportFlavor): BuildAssets {
  const readable = flavor === 'modular';
  return {
    js: iconSet === 'lucide'
      ? (readable ? rawJsLucideReadable : rawJsLucide)
      : (readable ? rawJsFomanticReadable : rawJsFomantic),
    cssTokens: rawCssTokens,
    cssStructure: rawCssStructure,
    cssTheme: rawCssTheme,
    cssBundle: rawCssBundle,
    dts: rawDts,
  };
}

/**
 * Build and download the export zip.
 */
export async function exportPackage(
  ds: DesignSystem,
  flavor: ExportFlavor,
  version: string
): Promise<void> {
  const blob = await buildPackage(ds, flavor, version);
  triggerDownload(blob, `${ds.prefix}-ui-${version}.zip`);
}

/**
 * Build the export zip as a Blob without triggering a download. Exposed for
 * tests and any caller that wants to handle the blob itself.
 */
export async function buildPackage(
  ds: DesignSystem,
  flavor: ExportFlavor,
  version = 'dev'
): Promise<Blob> {
  const { prefix } = ds;
  const assets = loadAssets(ds.tokens.icons.set, flavor);
  const zip = new JSZip();

  // ── vendor/ — pristine, substituted, NOTHING user-specific appended ──────
  const js = substitute(assets.js, prefix);
  const tokensCss = substitute(assets.cssTokens, prefix);
  const structureCss = substitute(assets.cssStructure, prefix);
  const themeCss = substitute(assets.cssTheme, prefix);
  const bundleCss = substitute(assets.cssBundle, prefix);
  const dts = substitute(assets.dts, prefix);
  const manifest = substitute(rawManifest, prefix);

  zip.file(`vendor/${prefix}-ui.js`, js);
  zip.file(`vendor/${prefix}-ui.d.ts`, dts);
  zip.file(`vendor/css/${prefix}-ui.css`, bundleCss);
  if (flavor === 'modular') {
    zip.file(`vendor/css/${prefix}-tokens.css`, tokensCss);
    zip.file(`vendor/css/${prefix}-structure.css`, structureCss);
    zip.file(`vendor/css/${prefix}-theme.css`, themeCss);
  }
  // Per-component docs (auto-generated, derived from source). Bundle so
  // consumers can grep the contract for any component without leaving the
  // package, and so agents have a Fomantic-style reference per component.
  zip.file('vendor/docs/manifest.json', manifest);
  for (const [path, body] of Object.entries(componentDocs)) {
    const basename = path.split('/').pop()!; // e.g. ry-switch.md
    zip.file(`vendor/docs/${substitutePath(basename, prefix)}`, substitute(body, prefix));
  }

  // ── {prefix}-brand.css — the theme, ONE file, generated from the knobs ──
  // Brand assets: data URLs → files in assets/, favicon variants from the
  // uploaded icon, and a CSS block pointing the logo tokens at those files.
  const brandAssetsCss = await emitBrandAssets(zip, ds);
  // Zero out brand fields so tokensToCss doesn't echo the (now extracted)
  // data URLs alongside the file-path override.
  const dsForCss = {
    ...ds,
    tokens: { ...ds.tokens, brand: { logoLight: '', logoDark: '', icon: '' } },
  };
  const brandCss = [
    `/* ${prefix}-brand.css — generated by the customizer for ${prefix}-ui ${version}.`,
    ` * DON'T HAND-EDIT: import ${prefix}-design-system.json into the customizer, adjust,`,
    ` * re-export. Hand overrides go in app/${prefix}-overrides.css. */`,
    '',
    tokensToCss(dsForCss, prefix),
    brandAssetsCss,
  ].filter(Boolean).join('\n') + '\n';
  zip.file(`${prefix}-brand.css`, brandCss);

  // ── app/ — yours. Generated in canonical `ry`, then substituted. ─────────
  const manifestData = JSON.parse(rawManifest) as ManifestComponent[];
  zip.file(`app/${prefix}.js`, substitute(generateAppJs(manifestData, version), prefix));
  zip.file(
    `app/${prefix}-overrides.css`,
    substitute(generateOverridesCss(assets.cssTokens, assets.cssTheme, version), prefix),
  );

  // ── Common files ─────────────────────────────────────────────────────────
  const designSystemJson = JSON.stringify({ ...ds, schemaVersion: ds.schemaVersion }, null, 2);
  zip.file(`${prefix}-design-system.json`, designSystemJson);
  zip.file('AGENTS.md', substitute(rawAgents, prefix));
  zip.file('README.md', substitute(readmeTemplate, prefix).replace(/\{\{version\}\}/g, version));
  zip.file(substitutePath('skills/ry-ui-builder/SKILL.md', prefix), substitute(skillTemplate, prefix));

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

/**
 * Write the user's uploaded brand assets into the zip:
 *   - assets/logo-light.{ext}  (if brand.logoLight)
 *   - assets/logo-dark.{ext}   (if brand.logoDark)
 *   - favicon.svg / favicon-16/32/48.png + apple-touch-icon.png (if brand.icon)
 *
 * Returns a CSS block (for {prefix}-brand.css at the zip root) that points
 * the logo tokens at `./assets/…` instead of data URLs.
 */
async function emitBrandAssets(zip: JSZip, ds: DesignSystem): Promise<string> {
  const { brand } = ds.tokens;
  const { prefix } = ds;

  const logoLight = decodeDataUrl(brand.logoLight);
  const logoDark  = decodeDataUrl(brand.logoDark);
  const icon      = decodeDataUrl(brand.icon);

  // Logo files
  if (logoLight) zip.file(`assets/logo-light.${logoLight.ext}`, logoLight.bytes);
  if (logoDark)  zip.file(`assets/logo-dark.${logoDark.ext}`,  logoDark.bytes);

  // Favicon variants — generated from the icon. SVG source is ideal:
  // we ship it as favicon.svg (browsers handle all sizes natively) AND
  // rasterize PNG fallbacks for legacy support.
  if (icon) {
    if (icon.ext === 'svg') {
      zip.file('favicon.svg', icon.bytes);
    } else {
      zip.file(`favicon.${icon.ext}`, icon.bytes);
    }
    // Rasterize from the data URL via Canvas at each standard size.
    try {
      const [f16, f32, f48, apple] = await Promise.all([
        resizeDataUrlToPng(brand.icon, 16, 16),
        resizeDataUrlToPng(brand.icon, 32, 32),
        resizeDataUrlToPng(brand.icon, 48, 48),
        resizeDataUrlToPng(brand.icon, 180, 180),
      ]);
      zip.file('favicon-16.png', f16);
      zip.file('favicon-32.png', f32);
      zip.file('favicon-48.png', f48);
      zip.file('apple-touch-icon.png', apple);
    } catch (e) {
      // Rasterization fails on opaque formats or worker contexts —
      // ship the original at least.
      console.warn('[export] favicon rasterization failed; only original shipped', e);
    }
  }

  if (!logoLight && !logoDark) return '';

  // Paths are relative to {prefix}-brand.css, which sits at the zip root
  // next to assets/.
  const build = (rel: string) => {
    const lines = ['/* === Brand assets (from customizer uploads) === */', ':root {'];
    if (logoLight) lines.push(`  --${prefix}-logo-light: url("${rel}assets/logo-light.${logoLight.ext}");`);
    if (logoDark)  lines.push(`  --${prefix}-logo-dark: url("${rel}assets/logo-dark.${logoDark.ext}");`);
    if (logoLight && logoDark) {
      lines.push(`  --${prefix}-logo: light-dark(var(--${prefix}-logo-light), var(--${prefix}-logo-dark));`);
    } else if (logoLight) {
      lines.push(`  --${prefix}-logo: var(--${prefix}-logo-light);`);
    } else if (logoDark) {
      lines.push(`  --${prefix}-logo: var(--${prefix}-logo-dark);`);
    }
    lines.push('}');
    return lines.join('\n');
  };

  return build('./');
}

/** Decode a `data:` URL into { mime, ext, bytes }. */
function decodeDataUrl(url: string): { mime: string; ext: string; bytes: Uint8Array } | null {
  if (!url || !url.startsWith('data:')) return null;
  const m = url.match(/^data:([^;,]+)(;base64)?,(.*)$/);
  if (!m) return null;
  const [, mime, isB64, payload] = m;
  const mimeMap: Record<string, string> = {
    'image/svg+xml': 'svg',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/x-icon': 'ico',
    'image/vnd.microsoft.icon': 'ico',
  };
  const ext = mimeMap[mime] ?? 'bin';

  let bytes: Uint8Array;
  if (isB64) {
    const binStr = atob(payload);
    bytes = new Uint8Array(binStr.length);
    for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(payload));
  }
  return { mime, ext, bytes };
}

/** Browser-side image resize via Canvas. Contain-fits the source into w×h. */
async function resizeDataUrlToPng(dataUrl: string, w: number, h: number): Promise<Blob> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load failed'));
    img.src = dataUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
  const sw = img.naturalWidth * scale;
  const sh = img.naturalHeight * scale;
  ctx.drawImage(img, (w - sw) / 2, (h - sh) / 2, sw, sh);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
  });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
